import { tunedParamsOf, withTuned, type PredictorConfig, type TunedParams } from "./config";
import { huber, replay, type ReplayInput, type ReplayScore } from "./replay";

/**
 * Automatic tuning of four predictor parameters against the phone's own history. Mirrors AutoTuner.java.
 *
 * 1. Score the current parameters with a leave-the-future-out replay.
 * 2. Score every combination of {down, same, up} for the four parameters (81 candidates, clamped to bounds).
 * 3. The best candidate must improve the score by ≥ 5 %, be no more than 2 % worse on the last 20 scored shots, and
 *    win consistently: the per-shot Huber losses, paired with the current ones, must improve by at least
 *    MIN_PAIRED_T standard errors. Searching 81 candidates on pure noise otherwise finds a "5 % better" one now and
 *    then.
 * 4. Move halfway toward it (arithmetic for the aging rate, geometric for the rest); the halfway point must pass the
 *    same gates against the current parameters, otherwise nothing changes.
 *
 * Deterministic: ties keep the earlier candidate in grid order (current first).
 */
export interface Bound { readonly min: number; readonly max: number }

export const TUNE_BOUNDS: Readonly<Record<keyof TunedParams, Bound>> = {
  agingRatePerDay: { min: 0.0, max: 0.04 },
  recencyHalfLifeD: { min: 2.0, max: 30.0 },
  priorPseudoShots: { min: 1.0, max: 12.0 },
  priorInterceptWeight: { min: 0.25, max: 4.0 },
};
export const AGING_STEP = 0.005;
export const GRID_FACTOR = 1.5;
export const MIN_IMPROVEMENT = 0.05;
export const RECENT_TOLERANCE = 0.02;
/** the paired per-shot improvement must be at least this many standard errors */
export const MIN_PAIRED_T = 2.0;
/** fewer scored shots than this and tuning does nothing */
export const MIN_SCORED_SHOTS = 20;

export type TuneReason = "adopted" | "too-few-shots" | "no-improvement" | "worse-recently" | "unstable" | "halfway-failed";

export interface TuneOutcome {
  readonly changed: boolean;
  readonly reason: TuneReason;
  readonly before: TunedParams;
  /** equals `before` unless changed */
  readonly after: TunedParams;
  /** the best grid candidate (before halving) */
  readonly best: TunedParams;
  readonly scoreBefore: number;
  readonly scoreAfter: number;
  readonly recentBefore: number;
  readonly recentAfter: number;
  readonly scoredShots: number;
}

const clamp = (v: number, b: Bound) => Math.max(b.min, Math.min(b.max, v));

/** Current values brought inside the bounds (defaults already are). */
export function clampParams(p: TunedParams): TunedParams {
  return {
    agingRatePerDay: clamp(p.agingRatePerDay, TUNE_BOUNDS.agingRatePerDay),
    recencyHalfLifeD: clamp(p.recencyHalfLifeD, TUNE_BOUNDS.recencyHalfLifeD),
    priorPseudoShots: clamp(p.priorPseudoShots, TUNE_BOUNDS.priorPseudoShots),
    priorInterceptWeight: clamp(p.priorInterceptWeight, TUNE_BOUNDS.priorInterceptWeight),
  };
}

/** The 81 grid points in fixed order (aging outermost, starting-point weight innermost); index 40 is the centre. */
export function grid(c: TunedParams): TunedParams[] {
  const ks = [-1, 0, 1];
  const out: TunedParams[] = [];
  for (const a of ks) for (const h of ks) for (const k of ks) for (const w of ks) {
    out.push({
      agingRatePerDay: clamp(c.agingRatePerDay + a * AGING_STEP, TUNE_BOUNDS.agingRatePerDay),
      recencyHalfLifeD: clamp(c.recencyHalfLifeD * Math.pow(GRID_FACTOR, h), TUNE_BOUNDS.recencyHalfLifeD),
      priorPseudoShots: clamp(c.priorPseudoShots * Math.pow(GRID_FACTOR, k), TUNE_BOUNDS.priorPseudoShots),
      priorInterceptWeight: clamp(c.priorInterceptWeight * Math.pow(GRID_FACTOR, w), TUNE_BOUNDS.priorInterceptWeight),
    });
  }
  return out;
}

export function halfway(c: TunedParams, b: TunedParams): TunedParams {
  return {
    agingRatePerDay: (c.agingRatePerDay + b.agingRatePerDay) / 2,
    recencyHalfLifeD: Math.sqrt(c.recencyHalfLifeD * b.recencyHalfLifeD),
    priorPseudoShots: Math.sqrt(c.priorPseudoShots * b.priorPseudoShots),
    priorInterceptWeight: Math.sqrt(c.priorInterceptWeight * b.priorInterceptWeight),
  };
}

function passes(cand: ReplayScore, cur: ReplayScore): "ok" | "no-improvement" | "worse-recently" | "unstable" {
  if (!(cand.score <= (1 - MIN_IMPROVEMENT) * cur.score)) return "no-improvement";
  if (!(cand.recentScore <= (1 + RECENT_TOLERANCE) * cur.recentScore)) return "worse-recently";
  return pairedT(cand.errors, cur.errors) <= -MIN_PAIRED_T ? "ok" : "unstable";
}

/**
 * t statistic of the paired per-shot differences huber(e_cand) − huber(e_cur); negative = the candidate is better.
 * +Infinity when the two replays scored different shots or there are too few to judge.
 */
export function pairedT(cand: readonly number[], cur: readonly number[]): number {
  const n = cur.length;
  if (cand.length !== n || n < 2) return Infinity;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += huber(cand[i] as number) - huber(cur[i] as number);
  mean /= n;
  let ss = 0;
  for (let i = 0; i < n; i++) {
    const d = huber(cand[i] as number) - huber(cur[i] as number) - mean;
    ss += d * d;
  }
  const se = Math.sqrt(ss / (n - 1) / n);
  if (se === 0) return mean < 0 ? -Infinity : Infinity;
  return mean / se;
}

export function autoTune(input: ReplayInput, cfg: PredictorConfig): TuneOutcome {
  const current = clampParams(tunedParamsOf(cfg));
  const cur = replay(input, withTuned(cfg, current));
  const none = (reason: TuneReason, best: TunedParams = current): TuneOutcome => ({
    changed: false, reason, before: current, after: current, best,
    scoreBefore: cur.score, scoreAfter: cur.score, recentBefore: cur.recentScore, recentAfter: cur.recentScore,
    scoredShots: cur.errors.length,
  });
  if (cur.errors.length < MIN_SCORED_SHOTS) return none("too-few-shots");

  const points = grid(current);
  let best = current;
  let bestScore = cur;
  for (let i = 0; i < points.length; i++) {
    if (i === 40) continue; // the centre is the current point, already scored
    const p = points[i] as TunedParams;
    const s = replay(input, withTuned(cfg, p));
    if (s.score < bestScore.score) { best = p; bestScore = s; }
  }
  if (best === current) return none("no-improvement");
  const gate = passes(bestScore, cur);
  if (gate !== "ok") return none(gate, best);

  const half = halfway(current, best);
  const hs = replay(input, withTuned(cfg, half));
  if (passes(hs, cur) !== "ok") return none("halfway-failed", best);
  return {
    changed: true, reason: "adopted", before: current, after: half, best,
    scoreBefore: cur.score, scoreAfter: hs.score, recentBefore: cur.recentScore, recentAfter: hs.recentScore,
    scoredShots: cur.errors.length,
  };
}
